import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Database, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  HardDrive,
  Calendar,
  FileArchive,
  ArrowLeft,
  Shield,
  Activity,
  Eye,
  ExternalLink,
  Settings,
  Languages,
  Palette,
  Bell,
  Package,
  ShoppingCart,
  Star,
  TrendingUp,
  Users,
  DollarSign,
  Menu,
  Megaphone,
  Sun,
  Moon
} from 'lucide-react';
import { NotificationBell } from '@/components/NotificationBell';
import { AdminSidebar, AdminSidebarToggle } from '@/components/admin/AdminSidebar';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLight, toggleTheme } = useAdminTheme();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupList, setBackupList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // E-commerce stats
  const [ecommerceStats, setEcommerceStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    pendingReviews: 0,
    revenue: 0
  });
  
  // Import job states
  const [batchRescrapeStatus, setBatchRescrapeStatus] = useState(null);
  const [telegramImportJob, setTelegramImportJob] = useState(null);
  const [lastScrapeInfo, setLastScrapeInfo] = useState(null);
  
  // Preview state
  const [previewStatus, setPreviewStatus] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Login failed' }));
        throw new Error(errorData.detail || 'Login failed');
      }
      
      const data = await response.json();
      
      setToken(data.access_token);
      setIsAuthenticated(true);
      localStorage.setItem('admin_token', data.access_token);
      toast.success('Connexion réussie');
    } catch (err) {
      setLoginError(err.message);
      toast.error(err.message);
    }
  };
  
  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      setIsAuthenticated(true);
    } else {
      setIsLoading(false);
    }
  }, []);
  
  // Fetch backup status
  const fetchBackupStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('admin_token');
        return;
      }
      
      if (!response.ok) return;
      
      const data = await response.json();
      setBackupStatus(data);
    } catch (err) {
      console.error('Failed to fetch backup status:', err);
    }
  }, [token]);
  
  // Fetch e-commerce stats
  const fetchEcommerceStats = useCallback(async () => {
    if (!token) return;
    
    try {
      // Fetch products count
      const productsRes = await fetch(`${API_URL}/api/products?limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const productsData = await productsRes.json();
      
      // Fetch pending reviews count
      const reviewsRes = await fetch(`${API_URL}/api/products/admin/reviews/queue?status=pending&limit=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const reviewsData = await reviewsRes.json();
      
      setEcommerceStats({
        totalProducts: productsData?.pagination?.total || productsData?.products?.length || 0,
        totalOrders: 0, // Could be fetched from orders API
        pendingReviews: reviewsData?.stats?.pending || 0,
        revenue: 0 // Could be calculated from orders
      });
    } catch (err) {
      console.error('Failed to fetch e-commerce stats:', err);
    }
  }, [token]);
  
  // Fetch backup list
  const fetchBackupList = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/list?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBackupList(data.backups || []);
      }
    } catch (err) {
      console.error('Failed to fetch backup list:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);
  
  // Fetch import job statuses
  const fetchImportStatuses = useCallback(async () => {
    if (!token) return;
    
    try {
      // Batch rescrape status
      const batchRes = await fetch(`${API_URL}/api/sourcing/batch/rescrape-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (batchRes.ok) {
        setBatchRescrapeStatus(await batchRes.json());
      }
      
      // Telegram import status
      const importRes = await fetch(`${API_URL}/api/telegram/import-job-latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (importRes.ok) {
        setTelegramImportJob(await importRes.json());
      }
      
      // Last scrape info
      const scrapeRes = await fetch(`${API_URL}/api/telegram/scrape/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (scrapeRes.ok) {
        setLastScrapeInfo(await scrapeRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch import statuses:', err);
    }
  }, [token]);
  
  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchBackupStatus();
      fetchBackupList();
      fetchPreviewStatus();
      fetchEcommerceStats();
      fetchImportStatuses();
    }
  }, [isAuthenticated, token, fetchBackupStatus, fetchBackupList, fetchEcommerceStats, fetchImportStatuses]);

  // Poll import statuses when jobs are running
  useEffect(() => {
    if ((batchRescrapeStatus?.status === 'running' || telegramImportJob?.status === 'running') && token) {
      const interval = setInterval(fetchImportStatuses, 5000);
      return () => clearInterval(interval);
    }
  }, [batchRescrapeStatus?.status, telegramImportJob?.status, token, fetchImportStatuses]);
  
  // Fetch preview status
  const fetchPreviewStatus = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/preview/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreviewStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch preview status:', err);
    }
  }, [token]);
  
  // Generate preview token
  const handleGeneratePreview = async () => {
    setIsGeneratingPreview(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/preview/token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate token');
      }
      
      const data = await response.json();
      
      if (data.token) {
        // Open preview in new tab
        window.open(`/preview?token=${data.token}`, '_blank');
        toast.success('Preview ouvert dans un nouvel onglet');
        // Refresh status
        await fetchPreviewStatus();
      } else {
        throw new Error('Failed to generate token');
      }
    } catch (err) {
      toast.error('Erreur de génération du preview');
    } finally {
      setIsGeneratingPreview(false);
    }
  };
  
  // Format remaining time for preview
  const formatPreviewTime = (seconds) => {
    if (!seconds || seconds <= 0) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Create backup handler
  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/backup/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ backup_type: 'full' })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.detail || 'Backup failed');
      }
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(`Backup créé avec succès: ${data.filename}`);
        // Refresh data
        await fetchBackupStatus();
        await fetchBackupList();
      } else {
        throw new Error(data.error || 'Backup failed');
      }
    } catch (err) {
      toast.error(`Échec du backup: ${err.message}`);
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };
  
  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
            <CheckCircle className="w-3 h-3 mr-1" />
            Succès
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <XCircle className="w-3 h-3 mr-1" />
            Échec
          </Badge>
        );
      case 'running':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
            <Clock className="w-3 h-3 mr-1 animate-spin" />
            En cours
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            {status || 'Inconnu'}
          </Badge>
        );
    }
  };
  
  // Login form
  if (!isAuthenticated) {
    return (
      <div className={cn(
        "min-h-screen flex items-center justify-center p-4",
        isLight ? "bg-gray-100" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
      )}>
        <Card className={cn(
          "w-full max-w-md",
          isLight ? "bg-white border-gray-200" : "bg-zinc-900/80 border-zinc-800"
        )}>
          <CardHeader className="text-center">
            <div className={cn(
              "mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4",
              isLight ? "bg-blue-100" : "bg-primary/20"
            )}>
              <Shield className={cn("w-6 h-6", isLight ? "text-blue-600" : "text-primary")} />
            </div>
            <CardTitle className={cn("text-xl", isLight ? "text-gray-900" : "text-white")}>Admin Dashboard</CardTitle>
            <CardDescription>Connectez-vous pour accéder au panneau d'administration</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className={cn("block text-sm mb-2", isLight ? "text-gray-700" : "text-white")}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(
                    "w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary",
                    isLight ? "bg-white border-gray-300 text-gray-900" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                  placeholder="admin@mutagim.shop"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className={cn("block text-sm mb-2", isLight ? "text-gray-700" : "text-white")}>Mot de passe</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={cn(
                    "w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary",
                    isLight ? "bg-white border-gray-300 text-gray-900" : "bg-zinc-800 border-zinc-700 text-white"
                  )}
                  placeholder="••••••••"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              {loginError && (
                <p className="text-red-400 text-sm">{loginError}</p>
              )}
              <Button 
                type="submit" 
                className="w-full bg-[#C0000C] hover:bg-[#A0000A] text-white"
                data-testid="admin-login-button"
              >
                Se connecter
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const lastBackup = backupStatus?.last_backup;
  const totalBackups = backupStatus?.total_backups || 0;
  const schedulerRunning = backupStatus?.scheduler?.running;
  
  return (
    <div className={cn(
      "min-h-screen transition-colors duration-200",
      isLight ? "bg-gray-100 admin-light-theme" : "bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
    )}>
      {/* Sidebar */}
      <AdminSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(false)} />
      
      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header */}
        <header className={cn(
          "border-b backdrop-blur-sm sticky top-0 z-40",
          isLight ? "border-gray-200 bg-white/90" : "border-zinc-800 bg-zinc-900/50"
        )}>
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <AdminSidebarToggle onClick={() => setSidebarOpen(true)} />
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/')}
                data-testid="back-home-button"
                className={isLight ? "text-gray-600 hover:text-gray-900" : "text-gray-200 hover:text-white"}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className={cn("text-xl font-bold", isLight ? "text-gray-900" : "text-white")}>Dashboard Admin</h1>
                <p className={cn("text-sm", isLight ? "text-gray-600" : "text-white")}>Mutagim.shop</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {schedulerRunning && (
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 hidden sm:flex">
                  <Activity className="w-3 h-3 mr-1" />
                  Scheduler actif
                </Badge>
              )}
              
              {/* Notification Bell */}
              <NotificationBell />
              
              {/* Theme Toggle */}
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleTheme}
                className={cn(
                  "gap-2 transition-colors",
                  isLight 
                    ? "border-gray-300 bg-white hover:bg-gray-100 text-gray-700" 
                    : "border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-gray-300"
                )}
                data-testid="theme-toggle"
              >
                {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span className="hidden sm:inline">{isLight ? 'Sombre' : 'Clair'}</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  localStorage.removeItem('admin_token');
                  setIsAuthenticated(false);
                  setToken(null);
                }}
                data-testid="logout-button"
                className={isLight ? "text-gray-700 border-gray-300" : "text-white border-zinc-700"}
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </header>
        
        <main className="p-4 lg:p-8">
          {/* E-commerce Quick Stats */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
            <Card 
              className="bg-zinc-900/80 border-zinc-800 cursor-pointer hover:bg-zinc-800/80 transition-colors"
              onClick={() => navigate('/admin/sourcing')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-500/10">
                  <Package className="h-6 w-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{ecommerceStats.totalProducts}</p>
                  <p className="text-gray-200 text-sm">Produits</p>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-zinc-900/80 border-zinc-800 cursor-pointer hover:bg-zinc-800/80 transition-colors"
              onClick={() => navigate('/admin/orders')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-green-500/10">
                  <ShoppingCart className="h-6 w-6 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{ecommerceStats.totalOrders}</p>
                  <p className="text-gray-200 text-sm">Commandes</p>
                </div>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-zinc-900/80 border-zinc-800 cursor-pointer hover:bg-zinc-800/80 transition-colors"
              onClick={() => navigate('/admin/reviews/queue')}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-yellow-500/10">
                  <Star className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{ecommerceStats.pendingReviews}</p>
                  <p className="text-gray-200 text-sm">Avis en attente</p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/80 border-zinc-800">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-purple-500/10">
                  <TrendingUp className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">-</p>
                  <p className="text-gray-200 text-sm">CA ce mois</p>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Import Jobs Status */}
          {(batchRescrapeStatus?.status === 'running' || telegramImportJob?.status === 'running' || lastScrapeInfo) && (
            <div className="mb-8 space-y-3">
              {/* Batch rescrape progress */}
              {batchRescrapeStatus?.status === 'running' && (
                <Card className="bg-blue-500/10 border-blue-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-blue-400 font-medium">Réparation images en cours</span>
                          <span className="text-white font-bold">{batchRescrapeStatus.completed}/{batchRescrapeStatus.total}</span>
                        </div>
                        <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all" 
                            style={{width: `${(batchRescrapeStatus.completed / batchRescrapeStatus.total) * 100}%`}}
                          />
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-green-400">✓ {batchRescrapeStatus.success} succès</span>
                          {batchRescrapeStatus.errors > 0 && <span className="text-red-400">✗ {batchRescrapeStatus.errors} erreurs</span>}
                          <span className="text-gray-400 ml-auto">{batchRescrapeStatus.current_product?.substring(0, 30)}...</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Telegram import progress */}
              {telegramImportJob?.status === 'running' && (
                <Card className="bg-purple-500/10 border-purple-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <RefreshCw className="w-5 h-5 animate-spin text-purple-400" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-purple-400 font-medium">Import Telegram en cours</span>
                          <span className="text-white font-bold">{telegramImportJob.processed}/{telegramImportJob.total}</span>
                        </div>
                        <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" 
                            style={{width: `${(telegramImportJob.processed / telegramImportJob.total) * 100}%`}}
                          />
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-green-400">✓ {telegramImportJob.imported} importés</span>
                          {telegramImportJob.errors > 0 && <span className="text-red-400">✗ {telegramImportJob.errors} erreurs</span>}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Telegram import interrupted - show resume/delete options */}
              {telegramImportJob?.status === 'interrupted' && telegramImportJob.processed < telegramImportJob.total && (
                <Card className="bg-orange-500/10 border-orange-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-orange-400">
                          Import Telegram interrompu : {telegramImportJob.processed}/{telegramImportJob.total} traités
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={async () => {
                            if (confirm('Supprimer ce job d\'import ?')) {
                              const token = localStorage.getItem('admin_token');
                              await fetch(`${API_URL}/api/telegram/import-job/${telegramImportJob.job_id}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              setTelegramImportJob(null);
                            }
                          }}
                        >
                          Supprimer
                        </Button>
                        <Button 
                          size="sm" 
                          className="bg-orange-600 hover:bg-orange-700"
                          onClick={async () => {
                            const token = localStorage.getItem('admin_token');
                            const res = await fetch(`${API_URL}/api/telegram/import-job/${telegramImportJob.job_id}/resume`, {
                              method: 'POST',
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const data = await res.json();
                              setTelegramImportJob({...telegramImportJob, status: 'running'});
                            }
                          }}
                        >
                          Reprendre
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Last scrape info */}
              {lastScrapeInfo && lastScrapeInfo.status !== 'idle' && (
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${lastScrapeInfo.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
                      <span className="text-gray-300">
                        Dernier scrape Telegram: {lastScrapeInfo.started_at ? new Date(lastScrapeInfo.started_at).toLocaleString('fr-FR', {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : '-'}
                        {lastScrapeInfo.new_items > 0 && <span className="text-green-400 ml-2">+{lastScrapeInfo.new_items} nouveaux</span>}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
          
          {/* Actions Grid */}
          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3 mb-8">
            <Card 
              className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-500/30 cursor-pointer hover:from-blue-600/30 hover:to-blue-800/30 transition-colors"
              onClick={() => navigate('/admin/sourcing')}
            >
              <CardContent className="p-6 text-center">
                <Package className="h-10 w-10 mx-auto text-blue-400 mb-3" />
                <h3 className="font-semibold text-white mb-1">Gérer les produits</h3>
                <p className="text-sm text-gray-200">Importer, modifier, supprimer</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border-yellow-500/30 cursor-pointer hover:from-yellow-600/30 hover:to-yellow-800/30 transition-colors"
              onClick={() => navigate('/admin/reviews/queue')}
            >
              <CardContent className="p-6 text-center">
                <Star className="h-10 w-10 mx-auto text-yellow-400 mb-3" />
                <h3 className="font-semibold text-white mb-1">Modérer les avis</h3>
                <p className="text-sm text-gray-200">Approuver ou rejeter les avis</p>
              </CardContent>
            </Card>
            
            <Card 
              className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-500/30 cursor-pointer hover:from-green-600/30 hover:to-green-800/30 transition-colors"
              onClick={() => navigate('/admin/promotions')}
            >
              <CardContent className="p-6 text-center">
                <Megaphone className="h-10 w-10 mx-auto text-green-400 mb-3" />
                <h3 className="font-semibold text-white mb-1">Créer une promo</h3>
                <p className="text-sm text-gray-200">Codes promo et réductions</p>
              </CardContent>
            </Card>
          </div>
          
          {/* System Status Section */}
          <div className="grid gap-6 lg:grid-cols-2">
          
          {/* Backup Status Widget */}
          <Card className="bg-zinc-900/80 border-zinc-800" data-testid="backup-status-widget">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/20 rounded-lg">
                    <Database className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">Backup MongoDB</CardTitle>
                    <CardDescription>Sauvegarde automatique quotidienne à 03:00 UTC</CardDescription>
                  </div>
                </div>
                <Button
                  onClick={handleCreateBackup}
                  disabled={isCreatingBackup}
                  data-testid="create-backup-button"
                >
                  {isCreatingBackup ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      En cours...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Créer un backup
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-200" />
                </div>
              ) : lastBackup ? (
                <div className="space-y-4">
                  {/* Status Header */}
                  <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-white">Dernier backup:</span>
                      {getStatusBadge(lastBackup.status)}
                    </div>
                    <div className="text-sm text-white">
                      {formatDate(lastBackup.completed_at || lastBackup.created_at)}
                    </div>
                  </div>
                  
                  {/* Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-3 bg-zinc-800/30 rounded-lg">
                      <div className="flex items-center gap-2 text-white text-sm mb-1">
                        <HardDrive className="w-4 h-4" />
                        Taille
                      </div>
                      <div className="text-white font-medium" data-testid="backup-size">
                        {formatFileSize(lastBackup.file_size_bytes)}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-zinc-800/30 rounded-lg">
                      <div className="flex items-center gap-2 text-white text-sm mb-1">
                        <FileArchive className="w-4 h-4" />
                        Collections
                      </div>
                      <div className="text-white font-medium">
                        {lastBackup.collections_count || 0}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-zinc-800/30 rounded-lg">
                      <div className="flex items-center gap-2 text-white text-sm mb-1">
                        <Database className="w-4 h-4" />
                        Documents
                      </div>
                      <div className="text-white font-medium">
                        {lastBackup.documents_count?.toLocaleString() || 0}
                      </div>
                    </div>
                    
                    <div className="p-3 bg-zinc-800/30 rounded-lg">
                      <div className="flex items-center gap-2 text-white text-sm mb-1">
                        <Clock className="w-4 h-4" />
                        Durée
                      </div>
                      <div className="text-white font-medium">
                        {lastBackup.duration_seconds?.toFixed(1) || 0}s
                      </div>
                    </div>
                  </div>
                  
                  {/* Error message if failed */}
                  {lastBackup.status === 'failed' && lastBackup.error_message && (
                    <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm">{lastBackup.error_message}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-200">
                  <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun backup disponible</p>
                  <p className="text-sm mt-1">Créez votre premier backup ci-dessus</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Stats Card */}
          <Card className="bg-zinc-900/80 border-zinc-800" data-testid="backup-stats-widget">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-primary" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-zinc-800/30 rounded-lg">
                <span className="text-white">Total backups</span>
                <span className="text-white font-bold text-xl" data-testid="total-backups-count">
                  {totalBackups}
                </span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-zinc-800/30 rounded-lg">
                <span className="text-white">Rétention</span>
                <span className="text-white font-medium">30 jours</span>
              </div>
              
              <div className="flex justify-between items-center p-3 bg-zinc-800/30 rounded-lg">
                <span className="text-white">Scheduler</span>
                {schedulerRunning ? (
                  <Badge className="bg-green-500/20 text-green-400">Actif</Badge>
                ) : (
                  <Badge className="bg-red-500/20 text-red-400">Inactif</Badge>
                )}
              </div>
              
              <div className="flex justify-between items-center p-3 bg-zinc-800/30 rounded-lg">
                <span className="text-white">Prochain backup</span>
                <span className="text-white text-sm">
                  {backupStatus?.settings?.next_scheduled 
                    ? formatDate(backupStatus.settings.next_scheduled)
                    : '03:00 UTC'}
                </span>
              </div>
            </CardContent>
          </Card>
          
          {/* Recent Backups List */}
          <Card className="bg-zinc-900/80 border-zinc-800" data-testid="backup-list-widget">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg text-white">Historique des backups</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    fetchBackupStatus();
                    fetchBackupList();
                  }}
                  data-testid="refresh-list-button"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {backupList.length > 0 ? (
                <div className="space-y-2">
                  {backupList.map((backup, index) => (
                    <div 
                      key={backup.id || index}
                      className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg hover:bg-zinc-800/50 transition-colors"
                      data-testid={`backup-item-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        {getStatusBadge(backup.status)}
                        <div>
                          <p className="text-white text-sm font-medium">
                            {backup.file_name || `Backup ${formatDate(backup.created_at)}`}
                          </p>
                          <p className="text-gray-200 text-xs">
                            {backup.triggered_by === 'manual' ? 'Manuel' : 'Automatique'} 
                            {' • '}
                            {formatFileSize(backup.file_size_bytes)}
                            {' • '}
                            {backup.documents_count?.toLocaleString() || 0} docs
                          </p>
                        </div>
                      </div>
                      <div className="text-white text-sm">
                        {formatDate(backup.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-200">
                  <FileArchive className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Aucun backup dans l'historique</p>
                </div>
              )}
            </CardContent>
          </Card>
          
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminDashboardPage;
